import React, { useEffect, useState, useRef } from 'react';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import {
  Button,
  Typography,
  TextArea,
  TextField,
  Tag,
  Tooltip,
  Divider,
  Spinner,
} from '@scale/scaleui';
import { useHistory } from 'react-router-dom';
import { SmsCommunication } from '@scale/shared-types';
import {
  getSmsCommunicationById,
  createAndSendSmsCommunication,
  sendTestSms,
  transformCSVUrl,
  getCSVFileHeaderAndFirstRow,
} from 'react-scale/corp/lib/api/growth';
import { useUser } from 'react-scale/components/ui/UserContext';
import { useUnleashFeatureFlagWithLoading } from 'react-scale/hooks/useUnleashFeatureFlag';
import { useMutation } from 'react-query';
import SAlert from 'react-s-alert';
import parse from 'csv-parse/lib/sync';
import { replaceVariables } from 'react-scale/components/GrowthCenter/EmailDripCampaign/CampaignSetup/EmailTemplateModalV2/EmailEditor';
import SmsMessageComponent from './SmsMessageComponent';
import QueueWarning from './QueueWarning';

type Props = {
  smsCommunicationId: string | null;
};

// eslint-disable-next-line complexity -- This component is complex and needs to be refactored
const SmsSetupBody: React.FC<Props> = ({ smsCommunicationId }) => {
  const [smsCommunication, setSmsCommunication] = useState<Partial<SmsCommunication>>({});
  const [csvFile, setCSVFile] = useState<File | undefined>(undefined);
  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const [testPhoneNumber, setTestPhoneNumber] = useState<string | undefined>(undefined);
  const [validSmsVars, setValidSmsVars] = useState<boolean>(false);
  const [smsVarsMsg, setSmsVarsMsg] = useState<string>('');
  const [tested, setTested] = useState<boolean>(!!smsCommunicationId);
  const [csvSubstitutions, setCsvSubstitutions] = useState<Record<string, string>>({});
  const [csvError, setCsvError] = useState<string>('');

  const history = useHistory();
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const user = useUser();

  const { data: canAccessSms, isLoading: isFeatureFlagLoading } = useUnleashFeatureFlagWithLoading(
    'growth-sms',
    { userId: user!._id },
    false,
  );

  useEffect(() => {
    async function fetchSmsCommunication() {
      if (smsCommunicationId) {
        try {
          const communication = await getSmsCommunicationById(smsCommunicationId);
          setSmsCommunication(communication as Partial<SmsCommunication>);
          // Fetch CSV headers if csvCDSUrl exists
          if (communication.csvCDSUrl) {
            try {
              const csvSubstitutionExamples = await getCSVFileHeaderAndFirstRow(communication);
              setCsvSubstitutions(csvSubstitutionExamples);
            } catch (e) {
              SAlert.error('Error fetching CSV file header. Please check the file.', {
                timeout: 2000,
              });
            }
          }
        } catch (error) {
          SAlert.error(`Failed to fetch SMS communication, error: ${error.response?.message}`, {
            timeout: 20000,
            html: true,
          });
        }
      }
    }
    fetchSmsCommunication();
  }, [smsCommunicationId]);

  const availableVariables = [
    'first_name',
    ...Object.keys(csvSubstitutions).filter(key => key !== 'first_name' && key !== 'id'),
  ];

  const validateMsg = (text: string, fieldType: string): void => {
    const variableRegex = /\{\{(.*?)\}\}/g;
    let match;
    while ((match = variableRegex.exec(text)) !== null) {
      const variable = match[1];
      if (!availableVariables.includes(variable)) {
        setValidSmsVars(false);
        setSmsVarsMsg(`{{${variable}}} is in ${fieldType} but is not a detected variable`);
        return;
      }
    }
    setValidSmsVars(true);
    setSmsVarsMsg('');
  };

  // Re-validate SMS body when CSV substitutions change
  useEffect(() => {
    if (smsCommunication.body) {
      validateMsg(smsCommunication.body, 'body');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvSubstitutions, smsCommunication.body]);

  const handleClickUpload = () => {
    hiddenFileInput.current?.click();
  };

  const handleClickDownload = async () => {
    if (!smsCommunication.csvCDSUrl) {
      SAlert.error('No CSV file found');
      return;
    }
    const url = await transformCSVUrl(smsCommunication.csvCDSUrl);
    window.open(url, '_blank');
  };

  const validateCSV = (csvData: string): [boolean, string] => {
    const rows: string[][] = parse(csvData, {
      ltrim: true,
      rtrim: true,
      bom: true,
    });

    if (rows.length === 0) {
      return [false, 'CSV has no rows'];
    }
    const headers = rows[0];
    if (new Set(headers).size !== headers.length) {
      return [false, 'CSV contains duplicate headers'];
    }
    if (headers.some(header => !header || header.trim() === '')) {
      return [false, 'CSV contains empty header(s)'];
    }
    if (headers.some(header => /[^a-zA-Z0-9_]/.test(header))) {
      return [false, 'CSV contains header(s) with spaces and/or non-alphanumeric characters'];
    }
    const idIndex = headers.findIndex(header => header.toLowerCase() === 'id');
    if (idIndex === -1) {
      return [false, 'CSV does not contain an id column'];
    }
    if (rows.length < 2) {
      return [false, 'CSV must have at least one data row'];
    }

    const firstRowForTestMessageVariables = headers.reduce(
      (obj: Record<string, string>, header, i) => {
        header = header.toLowerCase() === 'id' ? 'id' : header;
        obj[header] = rows[1][i];
        return obj;
      },
      {},
    );
    setCsvSubstitutions(firstRowForTestMessageVariables);

    for (const row of rows) {
      if (Object.values(row).some(cell => !cell)) {
        return [true, 'CSV contains empty cell(s)'];
      }
    }
    return [true, ''];
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = e => {
        if (e.target?.result) {
          const csvData = e.target.result as string;
          const [validCSV, csvMsg] = validateCSV(csvData);
          if (!validCSV) {
            setCsvError(`${csvMsg}. Please upload a different CSV.`);
            SAlert.error(`${csvMsg}. Please upload a different CSV.`, {
              timeout: 5000,
            });
            return;
          } else if (csvMsg) {
            setCsvError(`Warning: ${csvMsg}. Consider using a different CSV.`);
            SAlert.warning(`${csvMsg}. Consider using a different CSV.`, {
              timeout: 5000,
            });
          } else {
            setCsvError('');
          }
          setCSVFile(file);
          setFileName(file.name);
        }
      };
      reader.readAsText(file);
    }
  };

  const sendSmsCommunication = async () => {
    if (!csvFile) {
      return;
    }

    await createAndSendSmsCommunication(smsCommunication, csvFile);
  };

  const sendSmsCommunicationMutation = useMutation(sendSmsCommunication, {
    onSuccess: () => {
      SAlert.success('SMS communication sending started!');
      history.push(`${Routes.Sms}`);
    },
    onError: error => {
      SAlert.error(`Failed to send SMS communication, error: ${JSON.stringify(error)}`, {
        timeout: 20000,
        html: true,
      });
    },
  });

  const sendTest = async () => {
    if (!smsCommunication.body || !testPhoneNumber) {
      return;
    }

    // Replace template variables with values from the first row of the CSV for testing
    // first_name will be replaced in the backend
    const populatedBody = replaceVariables(smsCommunication.body, csvSubstitutions);
    await sendTestSms(populatedBody, testPhoneNumber);
  };

  const sendTestSmsMutation = useMutation(sendTest, {
    onSuccess: () => {
      SAlert.success('Test SMS sent to the provided number!');
      window.confirm(
        'Please confirm that you have received the test SMS and that it meets your expectations. ' +
          'If not, please do not proceed with sending the SMS communication.' +
          'Please reach out to the Growth team in #growth-eng-external for further assistance.',
      ) && setTested(true);
    },
    onError: (error: any) => {
      SAlert.error(`Error: ${error.response?.message}`, {
        timeout: 20000,
        html: true,
      });
    },
  });

  const updateSmsCommunicationFields = (fields: Partial<SmsCommunication>) => {
    setSmsCommunication({ ...smsCommunication, ...fields });
  };

  if (isFeatureFlagLoading) {
    return <Spinner size="lg" className="m-auto" />;
  }

  if (!canAccessSms) {
    return (
      <Typography.Paragraph1 className="text-neutral-600 p-4 text-center">
        You do not have access to this tool. Please contact #growth-eng-external slack channel for
        support.
      </Typography.Paragraph1>
    );
  }

  return (
    <div className="p-3 flex flex-col bg-neutral-0 w-[50%] h-full">
      <div className="flex flex-col gap-4">
        <QueueWarning />
        <div>
          <Typography.Paragraph2 className="text-neutral-800 pb-1">
            Communication Name
          </Typography.Paragraph2>
          <TextArea
            className="w-full"
            placeholder="Enter the name of the SMS communication"
            value={smsCommunication.name}
            onChange={e => updateSmsCommunicationFields({ name: e.target.value })}
            rows={1}
            resizable={false}
            disabled={!!smsCommunicationId}
          />
        </div>

        <SmsMessageComponent
          smsCommunication={smsCommunication}
          updateSmsCommunicationFields={updateSmsCommunicationFields}
          setTested={setTested}
          smsCommunicationId={smsCommunicationId}
          validateMsg={validateMsg}
        />

        <div className="gap-4">
          <Typography.Paragraph2 className="text-neutral-800 pb-1">Audience</Typography.Paragraph2>
          <Typography.Paragraph2 className="text-neutral-600">
            File must have a column "id" (case insensitive) containing the contributor IDs. Only
            contributors who have opted into SMS and have verified their numbers will have the SMS
            sent to them. Column names are case-sensitive. Refer to columns as{' '}
            {'{{exact_column_name}}'} in the SMS message. {'{{first_name}}'} will be available by
            default.
          </Typography.Paragraph2>
          <div className="my-1 gap-1">
            <Button
              className="button-upload"
              variant="secondary"
              onClick={() => {
                if (!smsCommunicationId) {
                  handleClickUpload();
                } else {
                  handleClickDownload();
                }
              }}
            >
              {smsCommunicationId ? 'Download CSV' : 'Upload CSV'}
            </Button>
            <input
              className="hidden"
              accept="text/csv"
              type="file"
              onChange={handleFileChange}
              ref={hiddenFileInput}
              id="choose-file-input"
            />

            {fileName && (
              <Typography.Paragraph3 className="text-neutral-800">
                Uploaded file: {fileName}
              </Typography.Paragraph3>
            )}
          </div>
          {csvError && (
            <Typography.Paragraph2 className="text-danger-500 mt-2">
              {csvError}
            </Typography.Paragraph2>
          )}
          {availableVariables.length > 0 && (
            <div className="mt-2">
              <Typography.Paragraph2 className="text-neutral-800 pb-1">
                Detected Variables
              </Typography.Paragraph2>
              <Typography.Paragraph3>
                {availableVariables.map((key, index) => (
                  <span key={index}>
                    {index > 0 && ' '}
                    <span
                      style={{ background: '#D1D5DB' }}
                      className="rounded p-1 mb-1 inline-block"
                    >
                      {key}
                    </span>
                  </span>
                ))}
              </Typography.Paragraph3>
            </div>
          )}
        </div>

        <div className="gap-4">
          <Typography.Paragraph2 className="text-neutral-800 pb-1">Test SMS</Typography.Paragraph2>
          <TextField
            className="w-full gap-1"
            placeholder="Enter your own phone number with country code"
            helperText="Format: +[Country Code][Number] (e.g., +1234567890); a test SMS will be sent to this number. The template variables will be replaced with the values from the first row of the CSV file."
            value={testPhoneNumber}
            onChange={e => setTestPhoneNumber(e.target.value.replace(/[^0-9+]/g, ''))}
            disabled={!!smsCommunicationId}
          />
          <div className="flex items-center space-x-2">
            <Tooltip
              side="top"
              target={
                <Button
                  variant="secondary"
                  className="my-1"
                  size="sm"
                  onClick={() => sendTestSmsMutation.mutate()}
                  disabled={
                    sendTestSmsMutation.isLoading ||
                    !testPhoneNumber ||
                    !smsCommunication.body ||
                    !!smsCommunicationId ||
                    !validSmsVars
                  }
                >
                  {sendTestSmsMutation.isLoading ? 'Sending...' : 'Send Test SMS'}
                </Button>
              }
              text={
                !smsCommunication.body
                  ? 'Please fill out the SMS body'
                  : !testPhoneNumber
                  ? 'Please enter a phone number'
                  : smsVarsMsg
              }
            />
            <Tag
              className="w-45"
              variant={tested ? 'success' : 'warning'}
              icon={['far', tested ? 'check-circle' : 'circle-xmark']}
            >
              {tested ? 'Tested' : 'Not Tested'}
            </Tag>
          </div>
        </div>
        <Divider className="" direction="horizontal" />
        <div className="flex">
          <Tooltip
            side="top"
            target={
              <div>
                <Button
                  variant="primary"
                  onClick={() => sendSmsCommunicationMutation.mutate()}
                  disabled={
                    sendSmsCommunicationMutation.isLoading ||
                    !csvFile ||
                    !smsCommunication.body ||
                    !smsCommunication.name ||
                    !tested ||
                    !!smsCommunicationId
                  }
                >
                  {sendSmsCommunicationMutation.isLoading ? 'Sending...' : 'Send SMS'}
                </Button>
              </div>
            }
            text={
              smsCommunicationId
                ? 'You are viewing an existing SMS communication. You cannot send it.'
                : !smsCommunication.body || !smsCommunication.name
                ? 'Please fill out all fields'
                : !tested
                ? 'Please click "Send Test SMS" and review the test SMS sent to you before sending'
                : !csvFile
                ? 'Please upload a CSV file with IDs'
                : ''
            }
          />
        </div>
      </div>
    </div>
  );
};

export default SmsSetupBody;
